import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { catchError, switchMap, throwError } from "rxjs";
import { AuthenticationService } from "@core/services/common/authentication.service";
import { SessionRefreshService } from "@core/services/common/session-refresh.service";
import { leerAccessToken } from "@core/utils/session-store";

/**
 * Rutas que no deben disparar un refresh. `refresh` y `logout` porque sería un bucle, y las de
 * login porque un 401 ahí significa credenciales incorrectas, no sesión vencida.
 */
const RUTAS_DE_AUTENTICACION = [
  "/auth/refresh",
  "/auth/logout",
  "/auth/validate",
  "/auth/exchange",
  "/auth/olvido",
  "/auth/cambiopass",
];

/**
 * Ante un 401 renueva la sesión y reintenta la request **una sola vez**.
 *
 * Va último en la cadena, es decir, el más cercano al backend: así ve el 401 antes que
 * `httpErrorInterceptor` y un vencimiento que se resuelve solo nunca llega a mostrar un snackbar.
 */
export const authRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  if (RUTAS_DE_AUTENTICACION.some((ruta) => req.url.includes(ruta))) {
    return next(req);
  }

  const sessionRefresh = inject(SessionRefreshService);
  const authenticationService = inject(AuthenticationService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        return throwError(() => error);
      }

      return sessionRefresh.refrescar().pipe(
        switchMap(() => next(conAccessTokenActual(req))),
        catchError((errorDelReintento: unknown) => {
          // Si el refresh falló, o si el reintento vuelve a dar 401, la sesión está muerta.
          // Cualquier otro error del reintento es un problema del endpoint, no de la sesión.
          const sesionPerdida =
            !(errorDelReintento instanceof HttpErrorResponse) ||
            errorDelReintento.status === 401;

          if (sesionPerdida) {
            authenticationService.cerrarSesionLocal();
          }

          return throwError(() =>
            errorDelReintento instanceof HttpErrorResponse
              ? errorDelReintento
              : error,
          );
        }),
      );
    }),
  );
};

function conAccessTokenActual<T>(req: HttpRequest<T>): HttpRequest<T> {
  const token = leerAccessToken();
  return token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;
}
